  <script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue'
import { getFormattedDate } from '@/utils/helpers'
import { verLotesSubasta } from "@/utils/subastaRedirections";
import { useUsuarioStore } from "@/store/usuarioStore";
import { storeToRefs } from "pinia";


const props = defineProps({
  carteleraStore: { type: Object, required: true },
})

const usuarioStore = useUsuarioStore();
const { getUsuario } = storeToRefs(usuarioStore);

const { appContext } = getCurrentInstance();
const storage_path = appContext.config.globalProperties.storage_path;

const viewportRef = ref(null)
const trackRef = ref(null)

// Muestra subastas privadas solo si el email del usuario logueado está en usuarios_permitidos
const subastas = computed(() => {
  const data = props.carteleraStore?.subastasDestacadas ?? [];
  const userEmail = getUsuario.value?.email?.toLowerCase();

  return data.filter((subasta) => {
    if (!subasta.privada) return true;
    if (!userEmail) return false;
    return (subasta.usuarios_permitidos ?? []).some(
      (u) => u.email?.toLowerCase() === userEmail
    );
  });
});


const VISIBLE = 3
const FALLBACK_ITEM_W = 720
const speedMs = 400


const itemW = ref(0)
const viewportW = ref(0)
const transitioning = ref(false)
const cur = ref(0)
let roViewport = null
let roItem = null

const needsLoop = computed(() => subastas.value.length > VISIBLE)
const trackItems = computed(() => {
  const arr = subastas.value
  if (!needsLoop.value) return arr
  const left = arr.slice(-VISIBLE)
  const right = arr.slice(0, VISIBLE)
  return [...left, ...arr, ...right]
})

async function medir() {
  await nextTick()
  const viewport = viewportRef.value
  const track = trackRef.value
  if (!viewport || !track) return

  const firstItem = track.querySelector('.item')
  if (!firstItem) return

  itemW.value = firstItem.offsetWidth || 0
  viewportW.value = viewport.offsetWidth || 0
}

const STEP = computed(() => itemW.value || FALLBACK_ITEM_W)

const translatePx = computed(() => {
  const step = STEP.value
  const vw = viewportW.value
  if (!step || !vw) return -(cur.value * step)
  return (vw - step) / 2 - (cur.value * step)
})

const trackStyle = computed(() => ({
  transform: `translateX(${translatePx.value}px)`,
  transition: transitioning.value ? `transform ${speedMs}ms ease` : 'none',
}))

function next() {
  if (transitioning.value || !trackItems.value.length) return
  transitioning.value = true
  cur.value += 1

  setTimeout(() => {
    const n = subastas.value.length
    if (needsLoop.value && cur.value === n + VISIBLE) {
      cur.value = VISIBLE
    }
    transitioning.value = false
  }, speedMs)
}

function prev() {
  if (transitioning.value || !trackItems.value.length) return
  transitioning.value = true
  cur.value -= 1

  setTimeout(() => {
    const n = subastas.value.length
    if (needsLoop.value && cur.value === VISIBLE - 1) {
      cur.value = n + VISIBLE - 1
    }
    transitioning.value = false
  }, speedMs)
}

onMounted(async () => {
  cur.value = needsLoop.value ? VISIBLE : 0
  await medir()

  window.addEventListener('resize', medir)

  const viewport = viewportRef.value
  const track = trackRef.value
  const firstItem = () => track?.querySelector('.item') || null
  roViewport = new ResizeObserver(() => medir())
  if (viewport) roViewport.observe(viewport)
  roItem = new ResizeObserver(() => medir())
  if (firstItem()) roItem.observe(firstItem())
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', medir)
  if (roViewport) roViewport.disconnect()
  if (roItem) roItem.disconnect()
})

watch(subastas, async () => {
  cur.value = needsLoop.value ? VISIBLE : 0
  await medir()
})

watch(trackItems, async () => {
  await medir()
  const track = trackRef.value
  const fi = track?.querySelector('.item')
  if (roItem && fi) {
    roItem.disconnect()
    roItem.observe(fi)
  }
})
</script>


<template>
  <div class="mb-2 mb-md-5 mt-4 shadow-none" v-if="subastas.length >= 3">
    <div class="slider" :class="{ 'slider--disabled': !needsLoop }">
      <!-- Flecha izquierda -->
      <button class="carousel-nav carousel-nav--prev" type="button" @click="prev" :disabled="!trackItems.length || transitioning"
        aria-label="Anterior">
        <span class="fa-solid fa-angle-left" aria-hidden="true"></span>
      </button>

      <div class="viewport" ref="viewportRef">
        <div class="track" ref="trackRef" :class="{ 'no-transition': !transitioning }" :style="trackStyle">
          <div 
              v-for="(s, i) in trackItems" 
              :key="s.id || i" 
              class="item cursor-pointer"
              :class="{ 'item--active': i === cur }"
              @click="verLotesSubasta(s.id)"
            >
            <div class="card border-0 shadow rounded-4 overflow-hidden card-fixed">
              <div class="d-md-none">
                <div class="ratio ratio-16x9 bg-placeholder">
                  <img v-if="s?.imagen?.hash && s?.imagen?.extension"
                    :src="`${storage_path}/${s.imagen.hash}.${s.imagen.extension}`" class="img-cover"
                    alt="Imagen de la subasta" />
                  <div v-else class="w-100 h-100"></div>
                </div>
              </div>

              <div class="row g-0 h-100">
                <div class="col-md-6 d-none d-md-block position-relative h-100">
                  <div class="media-cover bg-placeholder h-100 overflow-hidden">
                    <img v-if="s?.imagen?.hash && s?.imagen?.extension"
                      :src="`${storage_path}/${s.imagen.hash}.${s.imagen.extension}`" class="img-cover"
                      alt="Imagen de la subasta" />
                  </div>

                  <div v-if="s?.propietario?.logo?.hash && s?.propietario?.logo?.extension"
                    class="logo-empresa pt-2 pb-3 bg-white rounded shadow-sm d-inline-flex justify-content-center align-items-center p-2">
                    <img :src="`${storage_path}/${s.propietario.logo.hash}.${s.propietario.logo.extension}`"
                      style="max-width: 90px; max-height: 43px;" alt="Logo de la empresa" />
                  </div>
                </div>

                <div class="col-12 col-md-6">
                  <div class="p-4 d-flex flex-column h-100">
                    <h5 class="d-inline mb-0">
                      <span class="badge bg-badge-soft-green text-white d-inline-flex align-items-center fs-11 me-1">
                        <img class="me-1" src="@/assets/img/icons/click-mini.svg" alt="">
                        {{ s.modalidad_subasta.nombre }}
                      </span>
                    </h5>
                    <h5 class="fw-semibold text-black mb-2">
                      {{ s.titulo || 'Título' }}
                    </h5>
                    <p class="text-muted mb-4 small">
                      {{ s.subtitulo || s.descripcion || 'Descripción breve del item' }}
                    </p>

                    <div class="mt-auto">
                      <div class="bg-soft-green-carrusel rounded-1 p-4 text-subtitulos-secondary">
                        <div class="d-flex align-items-center gap-2 mb-2 lh-1">
                          <span class="fw-700 text-soft-green me-0 d-inline-flex align-items-center" style="width:80px;">Inicia</span>
                          <i class="fa-regular fa-calendar text-soft-green fs-16 d-inline-flex align-items-center"></i>
                          <span class="text-soft-green d-inline-flex align-items-center">
                            {{ getFormattedDate(s?.fecha_hora_inicio) || 'N/A' }}
                          </span>
                        </div>

                        <div class="d-flex align-items-center gap-2 mb-2 lh-1">
                          <span class="fw-700 text-soft-green me-0 d-inline-flex align-items-center" style="width:80px;">Finaliza</span>
                          <i class="fa-regular fa-calendar text-soft-green fs-16 d-inline-flex align-items-center icon-fix"></i>
                          <span class="text-soft-green d-inline-flex align-items-center">
                            {{ getFormattedDate(s?.fecha_hora_finalizacion) || 'N/A' }}
                          </span>
                        </div>
                        <div class="d-flex align-items-center gap-2 lh-1">
                          <i class="fa-regular fa-folder fs-16 text-soft-green d-inline-flex align-items-center"></i>
                          <span class="text-soft-green d-inline-flex align-items-center">ID {{ s.external_id }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- Flecha derecha -->
      <button class="carousel-nav carousel-nav--next" type="button" @click="next" :disabled="!trackItems.length || transitioning"
        aria-label="Siguiente">
        <span class="fa-solid fa-angle-right text-center" aria-hidden="true"></span>
      </button>
    </div>

    <div v-if="!subastas.length" class="text-center text-muted py-4">No hay subastas destacadas.</div>
  </div>

  <section class="hero position-relative" v-else>
    <img class="hero-img" src="@/assets/bannerhome.png" alt="home">

    <div class="hero-overlay"></div>

    <div class="container position-relative h-100 d-flex align-items-center z-2">
      <div>
        <h1 class="text-white fs-32 font-roboto-slab fw-bold mb-6 pb-6 mb-md-0 pb-md-0">Tu próxima oferta, <br> cuando quieras.</h1>
      </div>
    </div>
  </section>
</template>

<style scoped>
.slider {
  --visible: 3;
  --item-w: 720px;
  --gap: 16px;
  --peek: 0px;
  position: relative;
}

/* Aseguramos que nada corte la sombra */
.viewport, .track {
  overflow: visible !important;
}

/* Por defecto ninguna card con sombra */
.item .card {
  box-shadow: none !important;
  background: #fff;
  border-radius: 12px;
  position: relative;
  z-index: 1;
}

/* Sombra solo para el activo */
.item--active .card {
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important; /* sombra fuerte */
  z-index: 10; /* encima de todo */
}

.track {
  display: flex;
  will-change: transform;
}

.item {
  flex: 0 0 auto;
  width: var(--item-w);
  padding: 0 calc(var(--gap) / 2);
  box-sizing: border-box;
}

.card-fixed {
  width: 100%;
  height: 340px;
}

.bg-placeholder {
  background-color: #e9ecef;
}

.media-cover {
  width: 100%;
  height: 100%;
}

.img-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.position-relative .logo-empresa {
  position: absolute;
  top: 16px;
  left: 16px;
}

.carousel-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  background: #fff;
  border: none;
  border-radius: 50%;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
}

.carousel-nav--prev {
  left: 8px;
}

.carousel-nav--next {
  right: 8px;
}

.carousel-nav:disabled {
  opacity: .5;
  cursor: default;
}

.slider--disabled .carousel-nav {
  display: none;
}


.card-fixed {
  height: 340px;
}

@media (max-width: 767.98px) {
  .card-fixed {
    height: auto;
  }
}

.bg-placeholder {
  background-color: #e9ecef;
}

.img-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.media-cover {
  width: 100%;
  height: 100%;
}

.logo-empresa {
  position: absolute;
  top: 16px;
  left: 16px;
}

.item {
  width: var(--item-w);
}

@media (max-width: 991.98px) {
  .item {
    width: calc(100% - var(--gap));
  }
}

.hero {
  position: relative;
  width: 100%;
  height: 300px;
  overflow: hidden;
}

.hero-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

@media (max-width: 991px) {
  .hero {
    height: 300px;      
  }

  .hero-img {
    object-fit: cover;    
    object-position: center center;
  }
}


.no-transition {
  transition: none !important;
}

.viewport {
  overflow: hidden !important;
  touch-action: pan-y;
}
.track {
  user-select: none;
  -webkit-user-drag: none;
}

</style>
